import { Loader } from 'hfs-common-ui';
import { ComponentType, lazy, ReactNode, Suspense } from 'react';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import PageLoader from 'core/components/PageLoader';
import RootLayout from 'core/layout/RootLayout';
import { RoutePaths } from 'core/models';
import NotFound from 'core/pages/NotFound';
import Callback from 'feature/auth/pages/Callback';
import ErrorBoundary from './components/ErrorBoundary';
import OpenPWA from './pages/OpenPWA';
import * as S from './styles/css';

const Login = lazy(() => import('feature/auth/pages/Login'));
const Dashboard = lazy(() => import('feature/dashboard/pages/Dashboard/Dashboard'));
const Assets = lazy(() => import('feature/assets/pages/Assets/Assets'));
const AssetDetails = lazy(() => import('feature/assets/components/AssetDetails/AssetDetails'));
const AssetDetailsProductInfo = lazy(() => import('feature/assets/components/AssetDetailsProductInfo'));
const AssetDetailsContact = lazy(() => import('feature/assets/components/AssetDetailsContact'));
const AssetDetailsServiceContract = lazy(() => import('feature/assets/components/AssetDetailsServiceContract'));
const Companies = lazy(() => import('feature/companies/pages/Companies/Companies'));
const CompanyDetails = lazy(() => import('feature/companies/components/CompanyDetails/CompanyDetails'));
const Faq = lazy(() => import('feature/faq/pages/Faq/Faq'));
const CookiePolicy = lazy(() => import('feature/cookiepolicy/pages/CookiePolicy'));
const Settings = lazy(() => import('feature/settings/pages/Settings'));
const Changelog = lazy(() => import('feature/changelog/pages/Changelog'));
const AssetsMap = lazy(() => import('feature/map/pages/AssetsMap'));
const Dealers = lazy(() => import('feature/dealers/pages/dealers'));
const Maintenance = lazy(() => import('feature/maintenance/pages/Maintenance'));
const ThirdPartySoftware = lazy(() => import('feature/thirdpartysoftware/pages/ThirdPartySoftware'));
const TokenInfo = lazy(() => import('feature/auth/pages/TokenInfo'));
const FleetLogoutRelay = lazy(() => import('feature/auth/pages/FleetLogoutRelay/FleetLogoutRelay'));

export enum UserRoutes {
  UserLoginCallback = 'callback',
}

const componentWithSuspenseAndErrorBoundary = (
  Component: ComponentType,
  fallback: ReactNode = <S.LoaderDiv>{Component !== Dashboard && <Loader size="medium" center />}</S.LoaderDiv>
) => (
  <Suspense fallback={fallback}>
    <ErrorBoundary>
      <Component />
    </ErrorBoundary>
  </Suspense>
);

const getRoutes = (isDesktop: boolean): RouteObject[] => [
  {
    path: '/',
    element: <RootLayout />,
    id: 'root',
    children: [
      {
        path: RoutePaths.Dashboard,
        element: componentWithSuspenseAndErrorBoundary(Dashboard),
      },
      {
        path: RoutePaths.Assets,
        element: componentWithSuspenseAndErrorBoundary(Assets),
        children: [
          {
            path: RoutePaths.Details,
            element: componentWithSuspenseAndErrorBoundary(AssetDetails),
            children: [
              {
                path: RoutePaths.AssetDetailProductInfomation,
                element: componentWithSuspenseAndErrorBoundary(AssetDetailsProductInfo),
              },
              {
                path: RoutePaths.AssetDetailContacts,
                element: componentWithSuspenseAndErrorBoundary(AssetDetailsContact),
              },
              {
                path: RoutePaths.AssetDetailServiceContract,
                element: componentWithSuspenseAndErrorBoundary(AssetDetailsServiceContract),
              },
            ],
          },
        ],
      },
      {
        path: RoutePaths.Companies,
        element: componentWithSuspenseAndErrorBoundary(Companies),
        children: [
          {
            path: RoutePaths.Details,
            element: componentWithSuspenseAndErrorBoundary(CompanyDetails),
          },
        ],
      },
      {
        path: RoutePaths.AssetsMapVariant,
        element: componentWithSuspenseAndErrorBoundary(AssetsMap),
      },
      {
        path: RoutePaths.UserInfo,
        element: componentWithSuspenseAndErrorBoundary(TokenInfo),
      },
      {
        path: RoutePaths.More,
        element: isDesktop
          ? componentWithSuspenseAndErrorBoundary(TokenInfo)
          : componentWithSuspenseAndErrorBoundary(Settings),
      },
      {
        path: RoutePaths.Faq,
        element: componentWithSuspenseAndErrorBoundary(Faq),
      },
      {
        path: RoutePaths.Dealers,
        element: isDesktop
          ? componentWithSuspenseAndErrorBoundary(Dealers)
          : componentWithSuspenseAndErrorBoundary(NotFound),
      },
      { path: RoutePaths.WhatIsNew, element: componentWithSuspenseAndErrorBoundary(Changelog) },
      { path: RoutePaths.ThirdPartySoftware, element: componentWithSuspenseAndErrorBoundary(ThirdPartySoftware) },
      { path: RoutePaths.Maintenance, element: componentWithSuspenseAndErrorBoundary(Maintenance) },
    ],
  },
  {
    path: RoutePaths.CookiePolicy,
    element: componentWithSuspenseAndErrorBoundary(CookiePolicy),
  },
  {
    path: RoutePaths.OpenPWA,
    element: componentWithSuspenseAndErrorBoundary(OpenPWA),
  },
  {
    path: RoutePaths.UserLogin,
    element: componentWithSuspenseAndErrorBoundary(Login, <PageLoader />),
    children: [
      {
        path: UserRoutes.UserLoginCallback,
        element: <Callback />,
      },
    ],
  },
  {
    path: RoutePaths.FleetLogoutRelay,
    element: componentWithSuspenseAndErrorBoundary(FleetLogoutRelay),
  },
  {
    path: RoutePaths.NotFound,
    element: <NotFound />,
  },
];

const getBrowserRouter = (isDesktop: boolean) => createBrowserRouter(getRoutes(isDesktop));

export { getBrowserRouter, getRoutes };
